import { consoleApiSlice } from "./consoleApiSlice";
import { createSlice } from "@reduxjs/toolkit";

// ---------------------------------------------------------------------------
// Selected-environment slice (persisted to localStorage, same pattern as
// Agent Memory)
// ---------------------------------------------------------------------------
const getSavedEnv = () => {
  try {
    return localStorage.getItem("selectedControlTowerEnv") || null;
  } catch (error) {
    return null;
  }
};

export const controlTowerSlice = createSlice({
  name: "controlTower",
  initialState: { selectedEnv: getSavedEnv() },
  reducers: {
    setSelectedControlTowerEnv: (state, action) => {
      state.selectedEnv = action.payload;
      try {
        localStorage.setItem("selectedControlTowerEnv", action.payload);
      } catch (error) {
        // ignore storage failures
      }
    },
  },
});

export const { setSelectedControlTowerEnv } = controlTowerSlice.actions;
export const controlTowerReducer = controlTowerSlice.reducer;
export const selectSelectedControlTowerEnv = (state) =>
  state.controlTower.selectedEnv;

// ---------------------------------------------------------------------------
// RTK Query endpoints
// ---------------------------------------------------------------------------
export const agenticRunsApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgenticRunsEnvironments: builder.query({
      query: () => "/agentic-runs/environments",
      providesTags: ["AgenticRunsEnvironments"],
    }),

    getAgenticRuns: builder.query({
      query: ({ env, status, websiteId, agentId, callerId, limit = 50, cursor }) => ({
        url: "/agentic-runs",
        params: {
          env,
          ...(status ? { status } : {}),
          ...(websiteId ? { website_id: websiteId } : {}),
          ...(agentId ? { agent_id: agentId } : {}),
          ...(callerId ? { caller_id: callerId } : {}),
          ...(cursor ? { cursor } : {}),
          limit,
        },
      }),
      providesTags: (result, error, { env }) => [
        { type: "AgenticRuns", id: env },
      ],
    }),

    getAgenticInFlight: builder.query({
      query: ({ env = "prod" } = {}) => ({
        url: "/agentic-runs/in-flight",
        params: { env },
      }),
      providesTags: (result, error, arg) => [
        { type: "AgenticRuns", id: `inflight:${arg?.env || "prod"}` },
      ],
    }),

    getAgenticRunDetail: builder.query({
      query: ({ env, runId }) => `/agentic-runs/${env}/${runId}`,
      providesTags: (result, error, { env, runId }) => [
        { type: "AgenticRunDetail", id: `${env}:${runId}` },
      ],
    }),

    // Lazy per-fan-out: the S3 artifact inventory (grouped by pipeline phase),
    // the scope → dispatch → return reconciliation, and the
    // run_id -> {test_id, viewport} map that gives worker rows an identity.
    // Kept off the run-detail payload so the 15s detail poll stays cheap.
    getFanOutArtifacts: builder.query({
      query: ({ env, runId, fanOutId }) =>
        `/agentic-runs/${env}/${runId}/fan-outs/${fanOutId}/artifacts`,
      providesTags: (result, error, { env, runId, fanOutId }) => [
        { type: "FanOutArtifacts", id: `${env}:${runId}:${fanOutId}` },
      ],
    }),

    getFanOutScopeDiff: builder.query({
      query: ({ env, runId, fanOutId }) =>
        `/agentic-runs/${env}/${runId}/fan-outs/${fanOutId}/scope-diff`,
      providesTags: (result, error, { env, runId, fanOutId }) => [
        { type: "FanOutScopeDiff", id: `${env}:${runId}:${fanOutId}` },
      ],
    }),

    getAgenticRunEvents: builder.query({
      query: ({ env, runId, nextToken, limit = 200 }) => ({
        url: `/agentic-runs/${env}/${runId}/events`,
        params: { limit, ...(nextToken ? { next_token: nextToken } : {}) },
      }),
      providesTags: (result, error, { env, runId }) => [
        { type: "AgenticRunEvents", id: `${env}:${runId}` },
      ],
    }),

    // One system-prompt document, by content hash. Fetched only when the
    // operator expands a segment in the run's context panel — the manifest
    // that lists them rides in the events stream and carries no bodies.
    getRunContextSegment: builder.query({
      query: ({ env, runId, sha256 }) => ({
        url: `/agentic-runs/${env}/${runId}/context-segment`,
        params: { sha256 },
      }),
    }),

    startAgenticTraceExport: builder.mutation({
      query: ({ env, runId, includeImages = true }) => ({
        url: `/agentic-runs/${env}/${runId}/trace-export`,
        method: "POST",
        params: { include_images: includeImages },
      }),
    }),

    getAgenticTraceExportJob: builder.query({
      query: (jobId) => `/agentic-runs/trace-export-jobs/${jobId}`,
    }),
  }),
});

export const {
  useGetAgenticRunsEnvironmentsQuery,
  useGetAgenticRunsQuery,
  useLazyGetAgenticRunsQuery,
  useGetAgenticInFlightQuery,
  useGetAgenticRunDetailQuery,
  useGetAgenticRunEventsQuery,
  useLazyGetAgenticRunEventsQuery,
  useGetFanOutArtifactsQuery,
  useGetFanOutScopeDiffQuery,
  useLazyGetRunContextSegmentQuery,
  useStartAgenticTraceExportMutation,
  useGetAgenticTraceExportJobQuery,
} = agenticRunsApiSlice;
